import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, MenuItem, Popover } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { filterOrdersList } from 'store/thunks/ordersListThunk';
import { validateMinLength } from 'common/forms/formValidations';
import TextField from 'common/forms/TextField';
import DateField from 'common/forms/DateField';
import SelectField from 'common/forms/SelectField';
import FacilityFilterField from 'modules/WorkQueue/components/FacilityFilterField';

const dateToUTCString = (date) => {
  if (!date) return null;
  return date.toISOString();
};

const utcStringToLocalDate = (isoString) => {
  if (!isoString) return null;
  return new Date(isoString);
};

const OrdersFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const loading = useSelector(({ ordersList }) => ordersList.loading);

  const filter = useSelector(({ ordersList }) => ({
    ...ordersList.filter,
    createdAt: ordersList.filter.createdAt && utcStringToLocalDate(ordersList.filter.createdAt),
    updatedAt: ordersList.filter.updatedAt && utcStringToLocalDate(ordersList.filter.updatedAt),
  }));

  const popoverId = anchorEl ? 'OrdersList-OrderFilterButton-Popover' : undefined;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      filterOrdersList({
        filter: {
          ...formData,
          createdAt: formData.createdAt && dateToUTCString(formData.createdAt),
          updatedAt: formData.updatedAt && dateToUTCString(formData.updatedAt),
        },
      })
    );
  };

  return (
    <>
      <Button
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={filter}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Order Id'
                      name='orderId'
                      id='OrdersList-OrderFilterButton-orderId-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <FacilityFilterField />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Reference Id'
                      name='referenceId'
                      id='OrdersList-OrderFilterButton-referenceId-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Partner Account'
                      name='partnerAccount'
                      id='OrdersList-OrderFilterButton-partnerAccount-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Rx Number'
                      name='rxNumber'
                      id='OrdersList-OrderFilterButton-rxNumber-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Status'
                      name='status'
                      id='OrdersList-OrderFilterButton-status-dropdown'
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value='OPEN'>Open</MenuItem>
                      <MenuItem value='ORDER_VERIFICATION'>Verification</MenuItem>
                      <MenuItem value='CANCELED'>Canceled</MenuItem>
                      <MenuItem value='EXPIRED'>Expired</MenuItem>
                      <MenuItem value='ADDED_TO_SHIPMENT'>Shipment Created</MenuItem>
                      <MenuItem value='SHIPPED'>Shipped</MenuItem>
                      <MenuItem value='TRANSFERRED'>Transferred</MenuItem>
                      <MenuItem value='FILL_LATER'>Fill Later</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Item Status'
                      name='processingState'
                      id='OrdersList-OrderFilterButton-itemStatus-dropdown'
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value='SHIPPED'>Shipped</MenuItem>
                      <MenuItem value='CANCELED'>Canceled</MenuItem>
                      <MenuItem value='READY_FOR_SHIPMENT'>Ready For Shipment</MenuItem>
                      <MenuItem value='IN_SHIPMENT'>In Shipment</MenuItem>
                      <MenuItem value='PROCESSING'>Processing</MenuItem>
                      <MenuItem value='SPLIT'>Split</MenuItem>
                      <MenuItem value='TRANSFERRED'>Transferred</MenuItem>
                      <MenuItem value='FILL_LATER'>Fill Later</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Created At'
                      name='createdAt'
                      id='OrdersList-OrderFilterButton-createdAt-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Updated At'
                      name='updatedAt'
                      id='OrdersList-OrderFilterButton-updatedAt-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default OrdersFilterButton;
